import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Grid, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { UserRolesEnum } from '@sparling/financial-common'
import { icons } from '../../../../../../constants/icons'
import { data } from './SettelmentAddressesTableLists'
import { TableSmall } from '../../../../../../components/Dashboard/TableSmall'
import { AppStateContext, useRest, useRefresh } from '../../../../../../components/Context'
import { GreenButton } from '../../../../../../components/Dashboard/Buttons/GreenButton'
import { MemoAddAccountPopup } from './AddAccountPopup'

const useStyles = makeStyles((theme: Theme) => ({
  formWrap: {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    paddingTop: theme.spacing(4),
  },
  container: {
    minHeight: 40,
    maxHeight: 40,
  },
  sectionWrap: {
    width: '100%',
    height: '100%',
    padding: theme.spacing(0, 4, 0, 4),
    textAlign: 'right',
  },
}))

interface SettelmentAddressesSectionProps {
  title: string
}

export const SettelmentAddresses: FC<SettelmentAddressesSectionProps> = ({ title }) => {
  const formMethods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
  })
  const classes = useStyles()
  const { rxiosAuth } = useRest()
  const { getIsRefresh, getUserRole } = useContext(AppStateContext)
  const { saveIsRefresh } = useRefresh()
  const [loading, setLoading] = useState<boolean>(false)
  // eslint-disable-next-line
  const [values, setValues] = useState<any>({})
  const [isOpenModal, setOpen] = useState<boolean>(false)
  const [refresh, setRefresh] = useState<boolean | undefined>()

  useEffect(() => setRefresh(getIsRefresh()), [setRefresh, getIsRefresh])

  const toggleModal = useCallback(() => {
    setOpen((prevState) => !prevState)
  }, [setOpen])

  const loadData = useCallback(async () => {
    let cleanupFunction = false
    setLoading(true)

    rxiosAuth.get(data.request).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        setValues(next)
        if (!cleanupFunction) setLoading(false)
      },
      (error) => {
        toast.error(error)
        setLoading(false)
      },
      () => {
        setLoading(false)
        saveIsRefresh(false)
      },
    )
    cleanupFunction = true
    // eslint-disable-next-line
  }, [rxiosAuth])

  useEffect(() => {
    ;(async () => {
      await loadData()
    })()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (refresh) {
      ;(async () => {
        await loadData()
      })()
    }
    // eslint-disable-next-line
  }, [refresh])

  const filteredColumns = useMemo(
    () => data.columns.filter((column) => !column.roles || column.roles.includes(getUserRole?.() as UserRolesEnum)),
    [getUserRole],
  )

  return (
    <>
      <Grid item xs={12} className={classes.formWrap}>
        <FormProvider {...formMethods}>
          <Grid item xs={12} className={classes.container}>
            {title ? <Typography variant={'h6'}>{title}</Typography> : ' '}
          </Grid>

          <Grid item xs={12} className={classes.sectionWrap}>
            <TableSmall columns={filteredColumns} data={values.data} loading={loading} />
            {[UserRolesEnum.ADMIN, UserRolesEnum.ACCOUNTANT].includes(getUserRole?.() as UserRolesEnum) && (
              <GreenButton width={136} prefix={icons.ADD} ml={8} mb={32} text={'Add account'} onClick={toggleModal} />
            )}
          </Grid>
        </FormProvider>
      </Grid>

      <MemoAddAccountPopup isOpen={isOpenModal} modalClose={toggleModal} />
    </>
  )
}
